import {
  PENDING_SHORTCUTS,
  PROPOSAL_STATUSES,
  PROPOSAL_TYPES,
  appendPending,
  duplicateCheckNumber,
  effectiveDateSuggestions,
  validateProposal,
  type BeneficiarySituation,
  type ExtractedProposal,
  type ProposalStatus,
  type ProposalType,
  type ValidationProblem,
} from '@sales-rules';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { errorMessage } from '../../lib/api';
import {
  brl,
  centsToInput,
  devicePrefs,
  dmy,
  formatCnpj,
  formatCpf,
  maskCnpj,
  maskCpf,
  maskDate,
  parseDmy,
  salesApi,
  statusLabel,
  toCents,
  typeLabel,
  type ProposalPayload,
  type SalesPrefill,
  type SalesProposal,
  type SellerRef,
} from '../../lib/sales';
import { PdfZone } from './PdfZone';

const SITUATIONS: Array<[BeneficiarySituation, string]> = [
  ['L', 'Limpo'],
  ['R', 'Repique'],
  ['IN', 'Inadimplente'],
];
const RELATIONSHIPS = [
  'TITULAR',
  'CÔNJUGE',
  'COMPANHEIRO(A)',
  'FILHO(A)',
  'ENTEADO(A)',
  'PAI',
  'MÃE',
  'AGREGADO(A)',
];

interface Row {
  key: string;
  name: string;
  cpf: string;
  value: string;
  relationship: string;
  notes: string;
  situation: BeneficiarySituation | '';
}

let rowCounter = 0;
const newRow = (partial: Partial<Row> = {}): Row => ({
  key: `r${(rowCounter += 1)}`,
  name: '',
  cpf: '',
  value: '',
  relationship: '',
  notes: '',
  situation: '',
  ...partial,
});

const rowsFrom = (proposal: SalesProposal | null): Row[] =>
  proposal && proposal.beneficiaries.length
    ? proposal.beneficiaries.map((item) =>
        newRow({
          name: item.name,
          cpf: formatCpf(item.cpf),
          value: centsToInput(item.monthlyCents),
          relationship: item.relationship ?? '',
          notes: item.notes && item.notes !== item.relationship ? item.notes : '',
          situation: item.situation ?? '',
        }),
      )
    : [newRow()];

const sameDigits = (a: string, b: string) => {
  const x = a.replace(/\D/g, '');
  return x.length === 11 && x === b.replace(/\D/g, '');
};

/**
 * Painel lateral de cadastro e edição. Fecha com Esc ou clicando fora; lembra a
 * última operadora, tipo e vendedor neste aparelho; rola até o beneficiário que falta
 * marcar. A área de PDF (V2) preenche os campos e o PDF fica anexado ao salvar.
 */
export function ProposalForm({
  proposal,
  today,
  operators,
  isManager,
  me,
  sellers,
  defaultSeller,
  companies,
  plans,
  onClose,
  onSaved,
  onDeleted,
  initialPdf,
  prefill,
}: {
  proposal: SalesProposal | null;
  today: string;
  operators: string[];
  isManager: boolean;
  me: SellerRef;
  sellers: SellerRef[];
  defaultSeller: string;
  companies: string[];
  plans: string[];
  onClose: () => void;
  onSaved: (proposal: SalesProposal, created: boolean) => void;
  onDeleted: (proposal: SalesProposal) => void;
  /** O PDF arrastado para a tela: a leitura já começa ao abrir. */
  initialPdf: File | null;
  /** Nova proposta a partir de um negócio do CRM (V4): nome e lead já ligados. */
  prefill?: SalesPrefill | null;
}) {
  const editing = proposal;
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const lastType = devicePrefs.get('lastType') as ProposalType;
  const [operator, setOperator] = useState(
    editing?.operator ??
      (operators.includes(devicePrefs.get('lastOp')) ? devicePrefs.get('lastOp') : ''),
  );
  const [requestNumber, setRequestNumber] = useState(editing?.requestNumber ?? '');
  const [effective, setEffective] = useState(editing ? dmy(editing.effectiveDate) : '');
  const [company, setCompany] = useState(editing?.company ?? prefill?.company ?? '');
  const [cnpj, setCnpj] = useState(formatCnpj(editing?.cnpj));
  const [type, setType] = useState<ProposalType>(
    editing?.type ?? (PROPOSAL_TYPES.includes(lastType) ? lastType : 'MPE'),
  );
  const [plan, setPlan] = useState(editing?.plan ?? '');
  const [seller, setSeller] = useState(
    editing?.seller?.membershipId ??
      (defaultSeller || devicePrefs.get('lastSeller') || me.membershipId),
  );
  const [lives, setLives] = useState(editing ? String(editing.lives) : '');
  const [livesTouched, setLivesTouched] = useState(Boolean(editing?.livesManual));
  const [status, setStatus] = useState<ProposalStatus>(editing?.status ?? 'PRE');
  const [pendingText, setPendingText] = useState(editing?.pendingText ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [rows, setRows] = useState<Row[]>(() => rowsFrom(editing));
  const [problems, setProblems] = useState<ValidationProblem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [duplicate, setDuplicate] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [armed, setArmed] = useState(false);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), 4000);
    return () => window.clearTimeout(timer);
  }, [armed]);

  const filled = rows.filter((row) => row.name.trim() || row.cpf.trim() || row.value.trim());
  const totalCents = filled.reduce((sum, row) => sum + toCents(row.value), 0);
  const shownLives = livesTouched ? lives : filled.length ? String(filled.length) : '';
  const suggestions = useMemo(() => effectiveDateSuggestions(today), [today]);
  const sellerOptions = useMemo(
    () =>
      sellers.some((item) => item.membershipId === me.membershipId)
        ? sellers
        : [me, ...sellers],
    [sellers, me],
  );

  const updateRow = (key: string, patch: Partial<Row>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const checkDuplicate = async (value: string) => {
    setDuplicate(null);
    if (!duplicateCheckNumber(value)) return;
    try {
      const found = (await salesApi.duplicates(value)).filter(
        (item) => item.id !== editing?.id,
      );
      if (found.length) {
        setDuplicate(
          `O nº ${value} já está cadastrado: ${found
            .map(
              (item) =>
                `${item.company} (${dmy(item.effectiveDate)}, ${statusLabel(item.status)})`,
            )
            .join('; ')}`,
        );
      }
    } catch {
      // Sem a checagem, o cadastro segue.
    }
  };

  /** Preenche com o que veio do PDF, mantendo a situação de quem já estava marcado. */
  const fill = (data: ExtractedProposal, file?: File): string[] => {
    if (file) setPdfFile(file);
    const missing: string[] = [];
    if (data.operator && operators.includes(data.operator)) setOperator(data.operator);
    if (data.requestNumber) {
      setRequestNumber(data.requestNumber);
      void checkDuplicate(data.requestNumber);
    } else missing.push('nº da proposta');
    if (data.effectiveDate) setEffective(dmy(data.effectiveDate));
    else missing.push('vigência');
    if (data.company) setCompany(data.company.toLocaleUpperCase('pt-BR'));
    else missing.push('empresa/titular');
    if (data.cnpj) setCnpj(maskCnpj(data.cnpj));
    if (data.type) setType(data.type);
    if (data.plan) setPlan(data.plan.toLocaleUpperCase('pt-BR'));
    if (data.beneficiaries?.length) {
      setRows((current) => {
        const next = data.beneficiaries!.map((item) => {
          const name = item.name.toLocaleUpperCase('pt-BR');
          const previous = current.find(
            (row) =>
              (item.cpf && sameDigits(row.cpf, item.cpf)) ||
              (row.name.trim() && row.name.trim().toLocaleUpperCase('pt-BR') === name),
          );
          return newRow({
            name,
            cpf: item.cpf ? maskCpf(item.cpf) : (previous?.cpf ?? ''),
            value:
              item.monthlyCents !== null && item.monthlyCents !== undefined
                ? centsToInput(item.monthlyCents)
                : (previous?.value ?? ''),
            relationship: (item.relationship ?? previous?.relationship ?? '').toUpperCase(),
            notes: previous?.notes ?? '',
            situation: previous?.situation ?? '',
          });
        });
        return next.length ? next : current;
      });
    } else missing.push('beneficiários');
    return missing;
  };

  const payload = (): ProposalPayload | null => {
    const effectiveDate = parseDmy(effective, today);
    const beneficiaries = filled.map((row) => ({
      name: row.name.trim().toLocaleUpperCase('pt-BR'),
      cpf: row.cpf.replace(/\D/g, '') || null,
      monthlyCents: Math.abs(toCents(row.value)),
      situation: row.situation as BeneficiarySituation,
      relationship: row.relationship.trim().toUpperCase() || null,
      notes: row.notes.trim() || null,
    }));
    const found = validateProposal(
      {
        operator,
        company,
        effectiveDate,
        status,
        pendingText,
        beneficiaries: filled.map((row) => ({
          name: row.name,
          situation: row.situation || null,
        })),
      },
      editing ? [...operators, editing.operator] : operators,
    );
    setProblems(found);
    if (found.length) {
      setError(found.map((problem) => problem.message).join(' · '));
      const first = found.find((problem) => problem.index !== undefined);
      if (first?.index !== undefined) {
        rowRefs.current
          .get(filled[first.index].key)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return null;
    }
    if (!effectiveDate) return null;
    return {
      requestNumber: requestNumber.trim() || null,
      company: company.trim(),
      cnpj: cnpj.replace(/\D/g, '') || null,
      effectiveDate,
      type,
      plan: plan.trim() || null,
      operator,
      ...(isManager ? { sellerMembershipId: seller } : {}),
      lives: livesTouched && lives !== '' ? Number(lives) : null,
      status,
      pendingText: status === 'PENDENTE' ? pendingText.trim() : null,
      notes: notes.trim() || null,
      ...(prefill && !editing
        ? { businessId: prefill.businessId, leadId: prefill.leadId }
        : {}),
      beneficiaries,
    };
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setConflict(false);
    const body = payload();
    if (!body) return;
    setSaving(true);
    try {
      let saved = editing
        ? await salesApi.update(editing.id, { ...body, version: editing.version })
        : await salesApi.create(body);
      if (pdfFile) saved = await salesApi.attachPdf(saved.id, pdfFile);
      devicePrefs.set('lastOp', operator);
      devicePrefs.set('lastType', type);
      if (isManager) devicePrefs.set('lastSeller', seller);
      onSaved(saved, !editing);
    } catch (saveError) {
      const statusCode = (saveError as { response?: { status?: number } }).response?.status;
      if (statusCode === 409) setConflict(true);
      setError(errorMessage(saveError, 'Não consegui salvar agora. Tente de novo.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!armed) {
      setArmed(true);
      return;
    }
    try {
      await salesApi.remove(editing.id);
      onDeleted(editing);
    } catch (removeError) {
      setError(errorMessage(removeError, 'Não consegui excluir agora.'));
    }
  };

  const needs = (index: number) =>
    problems.some((problem) => problem.field === 'beneficiaries' && problem.index === index);

  return (
    <div
      className="scrim"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <form className="sheet" autoComplete="off" noValidate onSubmit={submit}>
        <div className="sheethead">
          <h3>{editing ? 'Editar proposta' : 'Nova proposta'}</h3>
          <button className="x" type="button" aria-label="Fechar" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="field">
          <span>Operadora</span>
          <div
            className={`seg opseg${problems.some((p) => p.field === 'operator') ? ' need' : ''}`}
          >
            {operators.map((name) => (
              <button
                key={name}
                type="button"
                aria-pressed={operator === name}
                onClick={() => setOperator(name)}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        {(editing?.lead || prefill) && (
          <div className="pdfst">
            Negociado por <b>{editing?.lead?.name ?? prefill?.leadName}</b> (lead do CRM). Ele
            não precisa estar entre os beneficiários.
          </div>
        )}
        <PdfZone
          fill={fill}
          attached={editing?.pdf?.fileName ?? null}
          initialFile={initialPdf}
        />

        <div className="fgrid">
          <label className="field">
            <span>Nº da proposta</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="23557"
              value={requestNumber}
              onChange={(event) => setRequestNumber(event.target.value)}
              onBlur={(event) => void checkDuplicate(event.target.value)}
            />
          </label>
          <label className="field">
            <span>Vigência</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="dd/mm/aaaa"
              list="sv-vig"
              value={effective}
              onChange={(event) =>
                setEffective(
                  /^[\d/]*$/.test(event.target.value)
                    ? maskDate(event.target.value)
                    : event.target.value,
                )
              }
            />
            <datalist id="sv-vig">
              {suggestions.map((date) => (
                <option key={date} value={dmy(date)} />
              ))}
            </datalist>
          </label>
          {duplicate && <div className="field full pdfst warn">{duplicate}</div>}
          <label className="field full">
            <span>Empresa / titular</span>
            <input
              type="text"
              list="sv-emp"
              placeholder="Razão social ou nome do titular"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
            />
            <datalist id="sv-emp">
              {companies.slice(0, 300).map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </label>
          <label className="field full">
            <span>CNPJ</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="opcional"
              value={cnpj}
              onChange={(event) => setCnpj(maskCnpj(event.target.value))}
            />
          </label>
          <label className="field">
            <span>Tipo</span>
            <select
              value={type}
              onChange={(event) => setType(event.target.value as ProposalType)}
            >
              {PROPOSAL_TYPES.map((item) => (
                <option key={item} value={item}>
                  {typeLabel(item)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Plano</span>
            <input
              type="text"
              list="sv-plano"
              placeholder="opcional"
              value={plan}
              onChange={(event) => setPlan(event.target.value)}
            />
            <datalist id="sv-plano">
              {plans.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </label>
          {isManager ? (
            <label className="field">
              <span>Vendedor</span>
              <select value={seller} onChange={(event) => setSeller(event.target.value)}>
                {sellerOptions.map((item) => (
                  <option key={item.membershipId} value={item.membershipId}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="field">
              <span>Vendedor</span>
              <input type="text" value={me.name} disabled />
            </label>
          )}
          <label className="field">
            <span>Vidas no contrato</span>
            <input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={shownLives}
              onChange={(event) => {
                setLivesTouched(true);
                setLives(event.target.value);
              }}
            />
          </label>
          <div className="field full">
            <span>Status</span>
            <div className="seg stseg">
              {PROPOSAL_STATUSES.map((item) => (
                <button
                  key={item}
                  type="button"
                  data-s={item}
                  aria-pressed={status === item}
                  onClick={() => setStatus(item)}
                >
                  {statusLabel(item)}
                </button>
              ))}
            </div>
          </div>
          {status === 'PENDENTE' && (
            <div className="field full">
              <span>Qual a pendência?</span>
              <div className="pmchips">
                {PENDING_SHORTCUTS.map((shortcut) => (
                  <button
                    key={shortcut}
                    type="button"
                    className="act"
                    onClick={() => setPendingText(appendPending(pendingText, shortcut))}
                  >
                    {shortcut}
                  </button>
                ))}
              </div>
              <input
                type="text"
                placeholder="Ex.: falta RG do dependente"
                value={pendingText}
                onChange={(event) => setPendingText(event.target.value)}
              />
            </div>
          )}
          <label className="field full">
            <span>Observação</span>
            <input
              type="text"
              placeholder="opcional"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>
        </div>

        <div className="bhead">
          <h2>Beneficiários</h2>
          <button
            className="linkbtn"
            type="button"
            onClick={() => setRows((current) => [...current, newRow()])}
          >
            + Adicionar
          </button>
        </div>
        <datalist id="sv-par">
          {RELATIONSHIPS.map((item) => (
            <option key={item} value={item} />
          ))}
        </datalist>
        <div className="brows">
          {rows.map((row) => {
            const index = filled.indexOf(row);
            return (
              <div
                key={row.key}
                className={`brow${index >= 0 && needs(index) ? ' need' : ''}`}
                ref={(element) => {
                  if (element) rowRefs.current.set(row.key, element);
                  else rowRefs.current.delete(row.key);
                }}
              >
                <label className="field nome">
                  <span>Nome</span>
                  <input
                    type="text"
                    value={row.name}
                    onChange={(event) => updateRow(row.key, { name: event.target.value })}
                  />
                </label>
                <button
                  type="button"
                  className="rm"
                  aria-label="Remover beneficiário"
                  onClick={() =>
                    setRows((current) =>
                      current.length > 1
                        ? current.filter((item) => item.key !== row.key)
                        : [newRow()],
                    )
                  }
                >
                  ×
                </button>
                <label className="field">
                  <span>CPF</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={row.cpf}
                    onChange={(event) =>
                      updateRow(row.key, { cpf: maskCpf(event.target.value) })
                    }
                  />
                </label>
                <label className="field">
                  <span>Valor (R$)</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={row.value}
                    onChange={(event) => updateRow(row.key, { value: event.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Vínculo</span>
                  <input
                    type="text"
                    list="sv-par"
                    placeholder="TITULAR ou parentesco"
                    value={row.relationship}
                    onChange={(event) =>
                      updateRow(row.key, { relationship: event.target.value })
                    }
                  />
                </label>
                <label className="field">
                  <span>Obs.</span>
                  <input
                    type="text"
                    placeholder="opcional"
                    value={row.notes}
                    onChange={(event) => updateRow(row.key, { notes: event.target.value })}
                  />
                </label>
                <div className="lrq">
                  <span>Essa pessoa é limpa, repique ou inadimplente?</span>
                  <div className="seg lrseg">
                    {SITUATIONS.map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        data-v={value}
                        aria-pressed={row.situation === value}
                        onClick={() => {
                          updateRow(row.key, { situation: value });
                          setProblems((current) =>
                            current.filter(
                              (problem) =>
                                !(problem.field === 'beneficiaries' && problem.index === index),
                            ),
                          );
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="ftot num">
          <span>
            {filled.length} {filled.length === 1 ? 'beneficiário' : 'beneficiários'}
          </span>
          <span>{brl(totalCents)}</span>
        </div>
        {error && (
          <div className="err" role="alert">
            {error}{' '}
            {conflict && (
              <button className="linkbtn" type="button" onClick={onClose}>
                Fechar e recarregar
              </button>
            )}
          </div>
        )}
        <div className="factions">
          {editing && (
            <button
              className={`btn danger${armed ? ' armed' : ''}`}
              type="button"
              onClick={() => void remove()}
            >
              {armed ? 'Toque de novo para excluir' : 'Excluir proposta'}
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn" type="submit" disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
}
